// Facture d'acompte : ne facturer qu'une part maintenant, le solde plus tard.
//
// L'acompte est une PART du devis, pas un montant libre : 30 % du devis, ce
// sont 30 % de chaque base de TVA. C'est ce qui permet, à la facture de solde,
// de déduire exactement ce qui a déjà été facturé, taux par taux, sans qu'un
// centime se perde entre les deux documents.
//
// On peut saisir cette part en pourcentage ou en euros — mais elle reste une
// part : taper 600 € revient à demander « la fraction du devis qui vaut
// 600 € ». Le récapitulatif affiche alors CE QUI SERA RÉELLEMENT FACTURÉ, au
// centime, plutôt que le nombre rond tapé : un arrondi par taux peut déplacer
// un centime, et la facture doit dire vrai.
//
// Les montants d'un acompte sont stockés POSITIFS ; ceux d'une déduction de
// solde sont NÉGATIFS, puisqu'ils retranchent.
//
// Fonctions PURES : aucun accès réseau ni base, donc testables ligne à ligne.

/** Une base de TVA et sa taxe : la forme de `detail_tva` partout dans le CRM. */
export interface BaseTva {
  tauxCode: string;
  /** Le taux en fraction : 0.1 pour 10 %. */
  tauxValeur: number;
  baseHt: number;
  montantTva: number;
}

/** Arrondi au centime, pour ne pas traîner les décimales du binaire. */
const centimes = (n: number): number => Math.round(n * 100) / 100;

/** En dessous, l'acompte ne vaut pas un document. */
export const ACOMPTE_MIN_TTC = 1;

/**
 * La part du devis que représente ce montant TTC.
 * Null si le total est nul ou absurde : on ne divise pas par zéro.
 */
export function partDepuisMontant(montantTtc: number, totalTtc: number): number | null {
  if (!Number.isFinite(montantTtc) || !Number.isFinite(totalTtc) || totalTtc <= 0) return null;
  return montantTtc / totalTtc;
}

/** Le montant TTC que représente cette part. */
export function montantDepuisPart(part: number, totalTtc: number): number {
  if (!Number.isFinite(part) || !Number.isFinite(totalTtc)) return 0;
  return centimes(part * totalTtc);
}

/**
 * La part appliquée à chaque base de TVA.
 *
 * Chaque taux est arrondi au centime pour son propre compte : c'est ce qui
 * s'inscrit sur la facture, et donc ce qui devra se déduire à l'identique au
 * solde. La TVA se recalcule sur la base arrondie, jamais en prenant une part
 * de la TVA d'origine — sinon base et taxe finissent par ne plus se répondre.
 */
export function repartirAcompte(detail: readonly BaseTva[], part: number): BaseTva[] {
  return detail
    .map((d) => {
      const baseHt = centimes(d.baseHt * part);
      return {
        tauxCode: d.tauxCode,
        tauxValeur: d.tauxValeur,
        baseHt,
        montantTva: centimes(baseHt * d.tauxValeur),
      };
    })
    .filter((d) => d.baseHt !== 0 || d.montantTva !== 0);
}

export interface TotauxAcompte {
  ht: number;
  tva: number;
  ttc: number;
}

/**
 * Ce que totalise une répartition. C'EST LA SOMME DES LIGNES qui fait foi, pas
 * le montant qu'on avait demandé : une facture doit s'additionner toute seule.
 */
export function totauxAcompte(bases: readonly BaseTva[]): TotauxAcompte {
  let ht = 0;
  let tva = 0;
  for (const b of bases) {
    ht += b.baseHt;
    tva += b.montantTva;
  }
  ht = centimes(ht);
  tva = centimes(tva);
  return { ht, tva, ttc: centimes(ht + tva) };
}

/**
 * Ce qui empêche d'émettre cet acompte, en une phrase — null si tout va bien.
 * Un acompte de 100 % n'est pas un acompte : c'est la facture entière, et le
 * CRM a déjà un bouton pour ça.
 */
export function validerAcompte(part: number, totalTtc: number): string | null {
  if (!Number.isFinite(part) || part <= 0) return 'Indiquez un acompte supérieur à zéro.';
  if (part >= 1) {
    return 'Un acompte de la totalité n’est pas un acompte : choisissez « La totalité ».';
  }
  if (totalTtc <= 0) return 'Cette facture n’a pas de montant à répartir.';
  if (montantDepuisPart(part, totalTtc) < ACOMPTE_MIN_TTC) {
    return `Un acompte doit atteindre au moins ${String(ACOMPTE_MIN_TTC)} € TTC.`;
  }
  return null;
}

/** La part écrite comme on la lit : 0.305 → « 30,5 % ». */
export function partEnPourcent(part: number, decimales = 2): string {
  const p = part * 100;
  const arrondi = Math.round(p * 10 ** decimales) / 10 ** decimales;
  return `${arrondi.toString().replace('.', ',')} %`;
}

/** Le libellé de la ligne d'acompte, tel qu'il s'imprime. */
export function libelleAcompte(part: number, refDevis: string | null): string {
  const base = `Acompte de ${partEnPourcent(part)}`;
  return refDevis === null || refDevis.trim() === ''
    ? base
    : `${base} sur le devis ${refDevis.trim()}`;
}

/** Un acompte déjà facturé, tel que le solde doit le retrancher. */
export interface AcompteFacture {
  numero: string;
  dateEmission: string;
  detailTva: BaseTva[];
}

/** Une ligne de déduction sur la facture de solde. */
export interface LigneDeduction {
  libelle: string;
  tauxCode: string;
  /** Négatif : la ligne retranche. */
  montantHt: number;
}

/**
 * Les lignes qui retranchent, sur la facture de solde, ce qui a déjà été
 * facturé en acompte — une par taux de TVA et par acompte, pour que la
 * déduction rende exactement les bases qu'elle avait prises.
 */
export function lignesDeduction(acomptes: readonly AcompteFacture[]): LigneDeduction[] {
  const out: LigneDeduction[] = [];
  for (const a of acomptes) {
    for (const d of a.detailTva) {
      if (d.baseHt === 0) continue;
      out.push({
        libelle: `Acompte déjà facturé — facture ${a.numero} du ${jourFrancais(a.dateEmission)}`,
        tauxCode: d.tauxCode,
        montantHt: centimes(-d.baseHt),
      });
    }
  }
  return out;
}

/** '2026-09-15' → '15/09/2026' ; ce qui n'est pas une date passe tel quel. */
export function jourFrancais(iso: string): string {
  const [a, m, j] = iso.slice(0, 10).split('-');
  return a !== undefined && m !== undefined && j !== undefined && a.length === 4
    ? `${j}/${m}/${a}`
    : iso;
}

/** Ce qui reste à facturer, une fois les acomptes retranchés. */
export function resteAFacturer(totalTtc: number, acomptes: readonly AcompteFacture[]): number {
  let deja = 0;
  for (const a of acomptes) {
    for (const d of a.detailTva) deja += d.baseHt + d.montantTva;
  }
  return centimes(totalTtc - deja);
}
